import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, Check, Plus, Landmark } from "lucide-react";
import type { BankAccount } from "../types";

interface AccountSelectorProps {
  accounts: BankAccount[];
  selectedId: string;
  onSelect: (id: string) => void;
  onConnectNew: () => void;
}

function formatBalance(value: number): string {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export function AccountSelector({ accounts, selectedId, onSelect, onConnectNew }: AccountSelectorProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const selected = accounts.find((a) => a.id === selectedId) ?? accounts[0];
  if (!selected) return null;

  return (
    <div ref={ref} className="relative w-full md:w-auto">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full md:w-auto inline-flex items-center gap-3 p-2 pr-3 border border-line rounded-lg bg-white hover:bg-[#FAFBFB] transition-colors"
      >
        <span className="w-8 h-8 rounded-md bg-sage text-ink2 inline-flex items-center justify-center flex-shrink-0">
          <Landmark size={15} strokeWidth={2} />
        </span>
        <div className="flex-1 text-left min-w-0">
          <div className="text-[13px] font-semibold text-ink truncate leading-tight">
            {selected.accountName} · {selected.accountNumberMasked.slice(-4)}
          </div>
          <div className="text-[11px] text-ink3 tabular-nums leading-tight mt-0.5">
            ${formatBalance(selected.balance)}
          </div>
        </div>
        <ChevronDown size={14} strokeWidth={2.5} className="text-ink3 flex-shrink-0" />
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-1.5 w-72 bg-white border border-line rounded-lg shadow-lg z-50 overflow-hidden">
          <div className="py-1 max-h-72 overflow-y-auto">
            {accounts.map((a) => {
              const isSel = a.id === selected.id;
              return (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => { onSelect(a.id); setOpen(false); }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-[#FAFBFB] ${isSel ? "bg-sage" : ""}`}
                >
                  <span className="w-7 h-7 rounded-md bg-sage text-ink2 inline-flex items-center justify-center flex-shrink-0">
                    <Landmark size={13} strokeWidth={2} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] font-semibold text-ink truncate leading-tight">
                      {a.accountName} · {a.accountNumberMasked.slice(-4)}
                    </div>
                    <div className="text-[11px] text-ink3 tabular-nums leading-tight mt-0.5 truncate">
                      ${formatBalance(a.balance)} · {a.bankName}
                    </div>
                  </div>
                  {isSel && <Check size={14} className="text-teal flex-shrink-0" />}
                </button>
              );
            })}
          </div>
          <div className="border-t border-lineSoft">
            <button
              type="button"
              onClick={() => { onConnectNew(); setOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-3 text-left hover:bg-[#FAFBFB] text-[12.5px] font-semibold text-teal"
            >
              <Plus size={14} strokeWidth={2.5} />
              Connect another bank
            </button>
          </div>
        </div>
      )}
    </div>
  );
}